import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faToggleOn,
  faToggleOff,
  faPlus,
  faTrash,
  faEdit,
  faChevronDown,
  faChevronRight,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import LoadingScreen from './LoadingScreen';

const Notifications = () => {
  const [emailSettings, setEmailSettings] = useState(null);
  const [notificationRules, setNotificationRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRules, setExpandedRules] = useState(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settings, rules] = await Promise.all([
          apiService.getNotificationSettings(),
          apiService.getNotificationRules()
        ]);
        setEmailSettings(settings);
        setNotificationRules(rules);

        const loadingTime = 1000 + Math.random() * 1500;
        setTimeout(() => setLoading(false), loadingTime);
      } catch (error) {
        console.error('Failed to load notifications:', error);
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleRule = (ruleId) => {
    setNotificationRules(rules =>
      rules.map(rule =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    );
  };

  const toggleRuleDetails = (ruleId) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  const deleteRule = (ruleId) => {
    alert('This is a demo environment. Deleting rules is not permitted.');
  };

  const editRule = () => {
    alert('This is a demo environment. Editing rules is not permitted.');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#94a3b8';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return faExclamationTriangle;
      case 'warning': return faExclamationTriangle;
      case 'info': return faInfoCircle;
      default: return faCheckCircle;
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!emailSettings) {
    return <div style={{ padding: '2rem', color: '#ef4444' }}>Failed to load notification settings</div>;
  }

  return (
    <div className="notifications-container" style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <FontAwesomeIcon icon={faBell} style={{ color: '#8b5cf6' }} />
          Notification Settings
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
          Configure email notifications and alert rules for system monitoring
        </p>
      </div>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Email Configuration Section */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ color: '#3b82f6', fontSize: '1.5rem' }} />
              <h2 style={{ fontSize: '1.5rem', color: '#e2e8f0', margin: 0 }}>Email Notifications</h2>
            </div>
            <button
              onClick={() => setEmailSettings({ ...emailSettings, enabled: !emailSettings.enabled })}
              style={{
                padding: '0.5rem 1rem',
                background: emailSettings.enabled ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                border: `1px solid ${emailSettings.enabled ? 'rgba(16, 185, 129, 0.4)' : 'rgba(100, 116, 139, 0.4)'}`,
                borderRadius: '6px',
                color: emailSettings.enabled ? '#10b981' : '#94a3b8',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
            >
              <FontAwesomeIcon icon={emailSettings.enabled ? faToggleOn : faToggleOff} style={{ fontSize: '1.2rem' }} />
              {emailSettings.enabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Email Address
              </label>
              <input
                type="email"
                value={emailSettings.email}
                disabled
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(100, 116, 139, 0.2)',
                  borderRadius: '6px',
                  color: '#94a3b8',
                  fontSize: '0.9rem',
                  cursor: 'not-allowed'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Status
              </label>
              <div style={{
                padding: '0.75rem',
                background: emailSettings.enabled ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                border: `1px solid ${emailSettings.enabled ? 'rgba(16, 185, 129, 0.3)' : 'rgba(100, 116, 139, 0.3)'}`,
                borderRadius: '6px',
                color: emailSettings.enabled ? '#10b981' : '#94a3b8',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}>
                {emailSettings.enabled ? '✓ Notifications Active' : '○ Notifications Paused'}
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: '#94a3b8'
          }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
            Email notifications will be sent to <strong style={{ color: '#e2e8f0' }}>{emailSettings.email}</strong> when any active rule is triggered.
          </div>
        </div>

        {/* Notification Rules Section */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: '#e2e8f0', margin: 0 }}>Notification Rules</h2>
            <button
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '6px',
                color: '#8b5cf6',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add New Rule
            </button>
          </div>

          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
            {notificationRules.filter(r => r.enabled).length} of {notificationRules.length} rules active
          </div>

          {/* Rules List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {notificationRules.map(rule => (
              <div
                key={rule.id}
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: `1px solid ${rule.enabled ? getSeverityColor(rule.severity) + '40' : 'rgba(100, 116, 139, 0.3)'}`,
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  opacity: rule.enabled ? 1 : 0.6
                }}
              >
                {/* Rule Header */}
                <div
                  onClick={() => toggleRuleDetails(rule.id)}
                  style={{
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(100, 116, 139, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <FontAwesomeIcon
                          icon={expandedRules.has(rule.id) ? faChevronDown : faChevronRight}
                          style={{ color: '#64748b', fontSize: '0.9rem' }}
                        />
                        <FontAwesomeIcon
                          icon={getSeverityIcon(rule.severity)}
                          style={{ color: getSeverityColor(rule.severity), fontSize: '1.1rem' }}
                        />
                        <h3 style={{ fontSize: '1.1rem', color: '#e2e8f0', margin: 0 }}>{rule.name}</h3>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: `${getSeverityColor(rule.severity)}20`,
                          border: `1px solid ${getSeverityColor(rule.severity)}40`,
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: getSeverityColor(rule.severity),
                          textTransform: 'uppercase'
                        }}>
                          {rule.severity}
                        </span>
                      </div>
                      <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 0.75rem 2.5rem' }}>
                        {rule.description}
                      </p>
                      <div style={{
                        marginLeft: '2.5rem',
                        padding: '0.5rem 0.75rem',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(100, 116, 139, 0.2)',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#cbd5e1'
                      }}>
                        {rule.condition}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRule(rule.id);
                        }}
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          color: rule.enabled ? '#10b981' : '#64748b',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                      >
                        <FontAwesomeIcon icon={rule.enabled ? faToggleOn : faToggleOff} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          editRule();
                        }}
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#3b82f6',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Edit rule"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRule(rule.id);
                        }}
                        style={{
                          padding: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#ef4444',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title="Delete rule"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', marginTop: '1rem', borderTop: '1px solid rgba(100, 116, 139, 0.2)', marginLeft: '2.5rem' }}>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <span style={{ marginRight: '1.5rem' }}>
                        Triggers: <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{rule.triggerCount}</span>
                      </span>
                      <span>
                        Last: <span style={{ color: rule.lastTriggered === 'Never' ? '#64748b' : '#10b981' }}>{rule.lastTriggered}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRules.has(rule.id) && rule.details && (
                  <div style={{
                    padding: '1.5rem',
                    paddingTop: '0',
                    borderTop: '1px solid rgba(100, 116, 139, 0.2)'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(100, 116, 139, 0.2)'
                    }}>
                      <h4 style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '1rem', fontWeight: '600' }}>
                        Detailed Information
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
                        {Object.entries(rule.details).map(([key, value]) => (
                          <div key={key} style={{ display: 'flex', gap: '1rem' }}>
                            <span style={{ color: '#64748b', minWidth: '150px', textTransform: 'capitalize' }}>
                              {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span style={{ color: '#e2e8f0', flex: 1 }}>
                              {typeof value === 'object' && value !== null ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                  {Object.entries(value).map(([k, v]) => (
                                    <div key={k}>• {k}: {v}</div>
                                  ))}
                                </div>
                              ) : Array.isArray(value) ? (
                                value.join(', ')
                              ) : (
                                value
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Statistics Section */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', color: '#e2e8f0', marginBottom: '1.5rem' }}>Notification Statistics (Last 7 Days)</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
            <div style={{
              padding: '1.5rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: '#3b82f6', marginBottom: '0.5rem' }}>
                {emailSettings.statistics.totalSent}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Total Notifications Sent</div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: '#ef4444', marginBottom: '0.5rem' }}>
                {emailSettings.statistics.criticalAlerts}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Critical Alerts</div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: '#f59e0b', marginBottom: '0.5rem' }}>
                {emailSettings.statistics.warningAlerts}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Warning Alerts</div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: '#10b981', marginBottom: '0.5rem' }}>
                {emailSettings.statistics.deliveryRate}%
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Delivery Success Rate</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
